// Two-factor auth tab — setup, verify, disable, backup codes display.
// State machine: status → (setup QR → verify → backup codes) or (disable form).

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Loading } from "@/components/ui/Loading";
import { ApiError } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "@/stores/toastStore";
import { useDict, fmt } from "@/i18n/LocaleProvider";
import {
  useDisable2FA,
  useSetup2FA,
  useTwoFactorStatus,
  useVerify2FA,
} from "@/hooks/useTwoFactor";
import type { TwoFactorSetupData } from "@/types/profile";

function formatErr(err: unknown): string {
  if (err instanceof ApiError)
    return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
  if (err instanceof Error) return err.message;
  return String(err);
}

export function TwoFactorTab() {
  const t = useDict().app.twoFactor;
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const forcedEnrollment = searchParams.get("setup_2fa") === "1" && user?.is_superuser;

  const status = useTwoFactorStatus();
  const setup = useSetup2FA();
  const verify = useVerify2FA();
  const disable = useDisable2FA();

  const [setupData, setSetupData] = useState<TwoFactorSetupData | null>(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [showDisable, setShowDisable] = useState(false);
  const [disPwd, setDisPwd] = useState("");
  const [disCode, setDisCode] = useState("");

  async function startSetup() {
    setErr(null);
    try {
      const data = await setup.mutateAsync();
      setSetupData(data);
    } catch (e) {
      setErr(formatErr(e));
    }
  }

  async function verifyActivation() {
    setErr(null);
    try {
      const r = await verify.mutateAsync(verifyCode);
      setBackupCodes(r.backup_codes);
      setSetupData(null);
      setVerifyCode("");
    } catch (e) {
      setErr(formatErr(e));
    }
  }

  async function disable2fa() {
    setErr(null);
    try {
      await disable.mutateAsync({ password: disPwd, code: disCode });
      setShowDisable(false);
      setDisPwd("");
      setDisCode("");
      setBackupCodes(null);
    } catch (e) {
      setErr(formatErr(e));
    }
  }

  function copyBackupCodes() {
    if (!backupCodes) return;
    const text =
      t.backupFileHeader + "\n\n" +
      backupCodes.join("\n") +
      "\n\n" + t.backupFileFooter;
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(t.backupCopied));
  }

  if (status.isLoading || !status.data) {
    return <Loading text={t.loading} />;
  }

  const showEnrollBanner = forcedEnrollment && !status.data.enabled;

  // ─── Showing backup codes (one-time display after activation) ─────────────
  if (backupCodes) {
    return (
      <Card className="border-success">
        <div className="font-display text-[18px] font-semibold text-success mb-[6px]">
          {t.backupTitle}
        </div>
        <div className="font-mono text-[11px] text-muted mb-4">
          {t.backupDesc}
        </div>
        <div
          className="rounded p-4 mb-4"
          style={{ background: "rgb(var(--color-bg))", border: "1px dashed rgb(var(--color-stroke))" }}
        >
          <div className="grid grid-cols-2 gap-2">
            {backupCodes.map((c) => (
              <div
                key={c}
                className="font-mono text-[14px] text-content font-semibold tracking-[0.05em] text-center py-2 bg-bg2 rounded-[3px]"
              >
                {c}
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="primary" onClick={copyBackupCodes}>
            {t.copyClipboard}
          </Button>
          <Button variant="default" onClick={() => setBackupCodes(null)}>
            {t.savedThem}
          </Button>
        </div>
      </Card>
    );
  }

  // ─── Setup QR code flow ───────────────────────────────────────────────────
  if (setupData) {
    return (
      <Card>
        <div className="font-mono text-[11px] text-muted uppercase tracking-[0.08em] mb-3">
          {t.scanQr}
        </div>
        <div className="flex gap-5 items-center">
          <div className="bg-white p-3 rounded">
            <img
              src={setupData.qr_code_data_uri}
              alt={t.qrAlt}
              className="w-[200px] h-[200px] block"
            />
          </div>
          <div className="flex-1">
            <div className="font-mono text-[11px] text-muted mb-2">
              {t.enterManually}
            </div>
            <div
              className="font-mono text-[12px] text-accent px-[10px] py-2 rounded break-all mb-[14px]"
              style={{ background: "rgb(var(--color-bg))", border: "1px solid rgb(var(--color-stroke))" }}
            >
              {setupData.secret}
            </div>
            <div className="font-mono text-[10px] text-dim mb-[14px]">
              {t.recommendedApps}
            </div>
          </div>
        </div>

        <div className="mt-5">
          <div className="font-mono text-[11px] text-muted uppercase tracking-[0.08em] mb-[10px]">
            {t.step2}
          </div>
          <Input
            label={t.verificationCode}
            value={verifyCode}
            onChange={setVerifyCode}
            placeholder={t.codePlaceholder}
          />
          {err && (
            <div className="px-3 py-2 bg-danger-bg border border-danger/40 rounded text-danger font-mono text-[11px] mb-[10px]">
              {err}
            </div>
          )}
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={verifyActivation}
              disabled={verify.isPending || verifyCode.length < 6}
            >
              {verify.isPending ? t.busy : t.activate}
            </Button>
            <Button
              variant="default"
              onClick={() => {
                setSetupData(null);
                setVerifyCode("");
                setErr(null);
              }}
            >
              {t.cancel}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // ─── Disable form ─────────────────────────────────────────────────────────
  if (showDisable) {
    return (
      <Card className="border-danger">
        <div className="font-mono text-[11px] text-danger uppercase tracking-[0.08em] mb-[6px]">
          {t.disableTitle}
        </div>
        <div className="font-mono text-[11px] text-muted mb-4">
          {t.disableDesc}
        </div>
        <Input
          label={t.currentPassword}
          value={disPwd}
          onChange={setDisPwd}
          type="password"
        />
        <Input
          label={t.currentCode}
          value={disCode}
          onChange={setDisCode}
          placeholder={t.disableCodePlaceholder}
        />
        {err && (
          <div className="px-3 py-2 bg-danger-bg border border-danger/40 rounded text-danger font-mono text-[11px] mb-[10px]">
            {err}
          </div>
        )}
        <div className="flex gap-2">
          <Button
            variant="danger"
            onClick={disable2fa}
            disabled={disable.isPending || !disPwd || !disCode}
          >
            {disable.isPending ? t.busy : t.disableBtn}
          </Button>
          <Button
            variant="default"
            onClick={() => {
              setShowDisable(false);
              setDisPwd("");
              setDisCode("");
              setErr(null);
            }}
          >
            {t.cancel}
          </Button>
        </div>
      </Card>
    );
  }

  // ─── Status view ──────────────────────────────────────────────────────────
  const st = status.data;
  return (
    <div>
      {showEnrollBanner && (
        <div
          className="px-[14px] py-3 rounded mb-4 font-mono text-[11px] leading-[1.6]"
          style={{
            background: "rgb(var(--color-danger) / 0.067)",
            border: "1px solid rgb(var(--color-danger) / 0.267)",
            color: "rgb(var(--color-danger))",
          }}
        >
          <b>{t.requiredLabel}</b>{t.requiredText}
        </div>
      )}

      <Card>
        <div className="flex justify-between items-start">
          <div>
            <div className="font-mono text-[11px] text-muted uppercase tracking-[0.08em] mb-2">
              {t.twoFactorAuth}
            </div>
            <div className="flex items-center gap-[10px] mb-2">
              <div
                className="w-[10px] h-[10px] rounded-full"
                style={{ background: st.enabled ? "rgb(var(--color-success))" : "rgb(var(--color-danger))" }}
              />
              <span className="font-display text-[16px] font-semibold text-content">
                {st.enabled ? t.enabled : t.disabled}
              </span>
            </div>
            {st.enabled && st.activated_at && (
              <div className="font-mono text-[10px] text-muted">
                {t.activatedOn} {new Date(st.activated_at).toLocaleDateString()}
              </div>
            )}
            {st.enabled && (
              <div
                className="font-mono text-[10px] mt-1"
                style={{
                  color: st.backup_codes_remaining < 3 ? "rgb(var(--color-warning))" : "rgb(var(--color-muted))",
                }}
              >
                {fmt(t.backupRemaining, { n: st.backup_codes_remaining })}
                {st.backup_codes_remaining < 3 && t.considerRegen}
              </div>
            )}
          </div>
          <div>
            {st.enabled ? (
              <Button variant="danger" onClick={() => setShowDisable(true)}>
                {t.disable2faBtn}
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={startSetup}
                disabled={setup.isPending}
              >
                {setup.isPending ? t.busy : t.enable2faBtn}
              </Button>
            )}
          </div>
        </div>
      </Card>

      {!st.enabled && (
        <Card
          className="mt-3"
          style={{ borderColor: "rgb(var(--color-warning) / 0.4)" }}
        >
          <div className="font-mono text-[11px] text-warning mb-[6px] font-semibold">
            {t.recommendation}
          </div>
          <div className="font-mono text-[11px] text-muted leading-[1.5]">
            {t.recommendationText}
          </div>
        </Card>
      )}

      {err && (
        <div className="mt-3 px-3 py-2 bg-danger-bg border border-danger/40 rounded text-danger font-mono text-[11px]">
          {err}
        </div>
      )}
    </div>
  );
}
